import React, { useState, useEffect } from "react";
import axios from "../utils/axiosInstance";
import { Chart, registerables } from "chart.js";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";

Chart.register(...registerables);

/* ── government palette ── */
const GOV = {
  blue:   "#0047AB",
  green:  "#20603D",
  yellow: "#FAD201",
  red:    "#c0392b",
  amber:  "#e07b00",
};

/* ── shared chart options ── */
const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: {
        color: "#4a5568",
        font: { size: 11, weight: "500" },
        padding: 12,
        usePointStyle: true,
        pointStyle: "circle",
        boxWidth: 8,
      },
    },
    tooltip: {
      backgroundColor: "#0d1b2a",
      titleFont: { size: 12, weight: "bold" },
      bodyFont: { size: 11 },
      padding: 10,
      cornerRadius: 8,
      displayColors: true,
      boxPadding: 4,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", font: { size: 10 }, maxRotation: 30 },
    },
    y: {
      grid: { color: "rgba(0,0,0,0.05)" },
      ticks: { color: "#64748b", font: { size: 10 } },
    },
  },
  animation: { duration: 900, easing: "easeOutQuart" },
  interaction: { mode: "nearest", axis: "x", intersect: false },
};

const pieOptions = {
  ...baseOptions,
  scales: undefined,
  plugins: {
    ...baseOptions.plugins,
    legend: {
      ...baseOptions.plugins.legend,
      position: "bottom",
      labels: {
        ...baseOptions.plugins.legend.labels,
        padding: 10,
        font: { size: 10 },
      },
    },
  },
};

/* ── StatCard ── */
function StatCard({ title, value, change, icon, color }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderLeft: `4px solid ${color}`,
      borderRadius: 10,
      padding: "clamp(12px,2vw,18px) clamp(10px,1.5vw,16px)",
      display: "flex", alignItems: "center", gap: 12,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      transition: "transform .2s, box-shadow .2s",
      cursor: "default", minWidth: 0,
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.10)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; }}
    >
      <div style={{
        width: 44, height: 44, minWidth: 44,
        borderRadius: 10,
        background: `${color}15`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1.2rem", color, flexShrink: 0,
      }}>
        <i className={`bi ${icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "clamp(0.6rem,1.2vw,0.7rem)", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
          {title}
        </div>
        <div style={{ fontSize: "clamp(1.2rem,2.5vw,1.6rem)", fontWeight: 800, color: "#0d1b2a", lineHeight: 1, letterSpacing: "-0.02em" }}>
          {value}
        </div>
        <div style={{ fontSize: "0.68rem", color: GOV.green, fontWeight: 600, marginTop: 3 }}>
          {change}
        </div>
      </div>
    </div>
  );
}

/* ── ChartCard ── */
function ChartCard({ title, icon, children, height = 240 }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      display: "flex", flexDirection: "column",
    }}>
      {/* header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: "1px solid #f1f5f9",
        background: "#f8fafc",
        gap: 8, flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: "0.85rem", color: "#1e293b", minWidth: 0 }}>
          <i className={`bi ${icon}`} style={{ color: GOV.blue, flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        </div>
        <span style={{
          fontSize: "0.6rem", fontWeight: 600, color: GOV.green,
          background: `${GOV.green}12`, padding: "2px 8px",
          borderRadius: 4, border: `1px solid ${GOV.green}22`,
          flexShrink: 0,
        }}>
          Real data
        </span>
      </div>
      {/* chart area */}
      <div style={{ padding: "12px 14px 14px", flex: 1 }}>
        <div style={{ position: "relative", height, width: "100%" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ── FilterBtn ── */
function FilterBtn({ active, onClick, icon, label }) {
  return (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "7px 14px",
      borderRadius: 8,
      border: active ? `1.5px solid ${GOV.blue}` : "1.5px solid #e2e8f0",
      background: active ? GOV.blue : "#fff",
      color: active ? "#fff" : "#64748b",
      fontWeight: 600, fontSize: "0.78rem",
      cursor: "pointer",
      transition: "all .18s",
      whiteSpace: "nowrap",
      minHeight: 36,
      WebkitTapHighlightColor: "transparent",
      touchAction: "manipulation",
    }}>
      <i className={`bi ${icon}`} />
      {label}
    </button>
  );
}

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
export default function ImibareNavigation() {
  const [activeChart, setActiveChart] = useState("all");
  const [loading, setLoading]         = useState(true);
  const [dbStats, setDbStats]         = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const h = token ? { Authorization: `Bearer ${token}` } : {};
        const [abaturageRes, ingoRes, abanaRes, urubyirukoRes] = await Promise.all([
          axios.get("/umuntu_umwirondoro", { headers: h }),
          axios.get("/urugo_umuryango",   { headers: h }),
          axios.get("/abana",             { headers: h }),
          axios.get("/urubyiruko",        { headers: h }),
        ]);
        setDbStats({
          abaturage:  abaturageRes.data  || [],
          ingo:       ingoRes.data       || [],
          abana:      abanaRes.data      || [],
          urubyiruko: urubyirukoRes.data || [],
        });
      } catch {
        setDbStats({ abaturage: [], ingo: [], abana: [], urubyiruko: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: 14 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: `3px solid ${GOV.blue}22`, borderTopColor: GOV.blue, animation: "imSpin .75s linear infinite" }} />
      <p style={{ color: "#64748b", fontSize: "0.84rem" }}>Gutegura imibare...</p>
      <style>{`@keyframes imSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const ab = dbStats.abaturage.length;
  const ig = dbStats.ingo.length;
  const an = dbStats.abana.length;
  const ur = dbStats.urubyiruko.length;

  /* ── chart datasets ── */
  const barMonthly = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Abaturage Bashya",
        data: [.15,.18,.22,.20,.25,1].map(f => Math.floor(ab * f)),
        backgroundColor: `${GOV.blue}CC`, borderColor: GOV.blue, borderWidth: 2,
        borderRadius: 5,
      },
      {
        label: "Barazenganye",
        data: [.08,.12,.18,.22,.25,.20].map(f => Math.floor(ab * f)),
        backgroundColor: `${GOV.red}CC`, borderColor: GOV.red, borderWidth: 2,
        borderRadius: 5,
      },
    ],
  };

  const lineWeekly = {
    labels: ["Mbe", "Kra", "Gtu", "Kne", "Gta", "Cya", "Cnu"],
    datasets: [
      {
        label: "Abaturage",
        data: [.14,.16,.15,.18,.20,.12,.05].map(f => Math.floor(ab * f)),
        borderColor: GOV.blue, backgroundColor: `${GOV.blue}22`,
        fill: true, tension: 0.4,
        pointBackgroundColor: GOV.blue, pointBorderColor: "#fff", pointRadius: 4,
      },
      {
        label: "Ingo",
        data: [.14,.16,.15,.18,.20,.12,.05].map(f => Math.floor(ig * f)),
        borderColor: GOV.green, backgroundColor: `${GOV.green}22`,
        fill: true, tension: 0.4,
        pointBackgroundColor: GOV.green, pointBorderColor: "#fff", pointRadius: 4,
      },
    ],
  };

  const pieDistrib = {
    labels: ["Abaturage", "Ingo", "Abana", "Urubyiruko", "Ibindi"],
    datasets: [{
      data: [ab, ig, an, ur, Math.floor((ab + ig) * 0.1)],
      backgroundColor: [`${GOV.blue}CC`, `${GOV.green}CC`, `${GOV.amber}CC`, "#6d28d9CC", "#94a3b8CC"],
      borderColor: ["#fff","#fff","#fff","#fff","#fff"],
      borderWidth: 2, hoverOffset: 10,
    }],
  };

  const barDept = {
    labels: ["Ubuzima", "Uburezi", "Ubukungu", "Umusanzu", "Umutekano", "Imibereho"],
    datasets: [{
      label: "Abakoresha",
      data: [.45,.38,.29,.35,.42,.31].map(f => Math.floor(ab * f)),
      backgroundColor: `${GOV.blue}CC`, borderColor: GOV.blue, borderWidth: 2,
      borderRadius: 6,
    }],
  };

  const linePerf = {
    labels: ["Icyumweru 1", "Icyumweru 2", "Icyumweru 3", "Icyumweru 4"],
    datasets: [
      {
        label: "Abaturage",
        data: [.25,.45,.75,1].map(f => Math.floor(ab * f)),
        borderColor: GOV.amber, backgroundColor: `${GOV.amber}22`,
        fill: true, tension: 0.4,
      },
      {
        label: "Ingo",
        data: [.25,.45,.75,1].map(f => Math.floor(ig * f)),
        borderColor: GOV.green, backgroundColor: `${GOV.green}22`,
        fill: true, tension: 0.4,
      },
    ],
  };

  const statsCards = [
    { title: "Abaturage Bose",  value: ab.toLocaleString(), change: "+12%", icon: "bi-people-fill",      color: GOV.blue  },
    { title: "Ingo Bose",       value: ig.toLocaleString(), change: "+8%",  icon: "bi-house-door-fill",  color: GOV.green },
    { title: "Abana Bose",      value: an.toLocaleString(), change: "+23%", icon: "bi-emoji-smile-fill", color: GOV.amber },
    { title: "Urubyiruko Bose", value: ur.toLocaleString(), change: "+5%",  icon: "bi-person-arms-up",   color: "#6d28d9" },
  ];

  const showBar  = activeChart === "all" || activeChart === "bar";
  const showLine = activeChart === "all" || activeChart === "line";
  const showPie  = activeChart === "all" || activeChart === "pie";

  return (
    <>
      <style>{`
        /* responsive grid helpers */
        .im-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0,1fr));
          gap: clamp(8px,1.8vw,14px);
          margin-bottom: clamp(14px,2.5vw,22px);
        }
        .im-charts-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0,1fr));
          gap: clamp(8px,1.8vw,14px);
          margin-bottom: clamp(14px,2.5vw,22px);
        }
        .im-chart-full { grid-column: 1 / -1; }

        @media (max-width: 1023px) {
          .im-stats-grid  { grid-template-columns: repeat(2, minmax(0,1fr)); }
          .im-charts-grid { grid-template-columns: 1fr; }
          .im-chart-full  { grid-column: 1; }
        }
        @media (max-width: 575px) {
          .im-stats-grid  { grid-template-columns: repeat(2, minmax(0,1fr)); gap: 8px; }
          .im-charts-grid { gap: 8px; }
        }
        @media (max-width: 359px) {
          .im-stats-grid  { gap: 6px; }
          .im-charts-grid { gap: 6px; }
        }
        @media (hover: none) and (pointer: coarse) {
          .im-stat-hover:hover { transform: none !important; box-shadow: 0 1px 4px rgba(0,0,0,0.06) !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div style={{
        maxWidth: 1200, margin: "0 auto",
        fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        WebkitFontSmoothing: "antialiased",
        paddingBottom: "clamp(24px,5vw,48px)",
      }}>

        {/* ── Page header ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, marginBottom: "clamp(14px,2.5vw,22px)", flexWrap: "wrap",
        }}>
          <div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "3px 10px", borderRadius: 999,
              background: `${GOV.blue}10`, border: `1px solid ${GOV.blue}25`,
              color: GOV.blue, fontSize: "clamp(0.58rem,1.2vw,0.65rem)",
              fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
              marginBottom: 6,
            }}>
              <i className="bi bi-bar-chart-fill" />
              Imibare Navigation
            </div>
            <h2 style={{
              fontSize: "clamp(1rem,2.8vw,1.35rem)", fontWeight: 800,
              color: "#0d1b2a", margin: 0, lineHeight: 1.2,
            }}>
              Imibare y'Isibo
            </h2>
          </div>

          {/* filter buttons */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <FilterBtn active={activeChart === "all"}  onClick={() => setActiveChart("all")}  icon="bi-grid-fill"       label="Bose" />
            <FilterBtn active={activeChart === "bar"}  onClick={() => setActiveChart("bar")}  icon="bi-bar-chart-fill"  label="Bar" />
            <FilterBtn active={activeChart === "line"} onClick={() => setActiveChart("line")} icon="bi-graph-up"        label="Line" />
            <FilterBtn active={activeChart === "pie"}  onClick={() => setActiveChart("pie")}  icon="bi-pie-chart-fill"  label="Pie" />
          </div>
        </div>

        {/* ── Stats cards ── */}
        <div className="im-stats-grid">
          {statsCards.map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        {/* ── Charts ── */}
        <div className="im-charts-grid">

          {showBar && (
            <div className="im-chart-full">
              <ChartCard title="Abaturage Bishya & Barazenganye" icon="bi-bar-chart-fill" height={220}>
                <Bar data={barMonthly} options={baseOptions} />
              </ChartCard>
            </div>
          )}

          {showLine && (
            <ChartCard title="Abaturage & Ingo (Imiyoboro)" icon="bi-graph-up" height={220}>
              <Line data={lineWeekly} options={baseOptions} />
            </ChartCard>
          )}

          {showPie && (
            <ChartCard title="Distribution y'Ibigenge" icon="bi-pie-chart-fill" height={220}>
              <Pie data={pieDistrib} options={pieOptions} />
            </ChartCard>
          )}

          {showBar && (
            <ChartCard title="Comparison y'Amashami" icon="bi-bar-chart-fill" height={220}>
              <Bar data={barDept} options={baseOptions} />
            </ChartCard>
          )}

          {showLine && (
            <ChartCard title="Performance Metrics" icon="bi-graph-up" height={220}>
              <Line data={linePerf} options={baseOptions} />
            </ChartCard>
          )}

          {showPie && (
            <ChartCard title="Activity Distribution (Doughnut)" icon="bi-pie-chart-fill" height={220}>
              <Doughnut data={pieDistrib} options={pieOptions} />
            </ChartCard>
          )}

        </div>

        {/* ── Table ── */}
        <div style={{
          background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: 10, overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}>
          {/* table header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
            background: "#f8fafc", gap: 10, flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: "0.88rem", color: "#1e293b" }}>
              <i className="bi bi-table" style={{ color: GOV.blue }} />
              Detailed Statistics
            </div>
            <button style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 7,
              background: `${GOV.green}10`, border: `1px solid ${GOV.green}25`,
              color: GOV.green, fontWeight: 600, fontSize: "0.76rem",
              cursor: "pointer",
            }}>
              <i className="bi bi-download" />
              Download Excel
            </button>
          </div>

          {/* scrollable table */}
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{
              width: "100%", borderCollapse: "collapse",
              fontSize: "clamp(0.75rem,1.5vw,0.84rem)",
              minWidth: 480,
            }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Itariki", "Abaturage Bashya", "Barazenganye", "Ibyakozwe", "Success Rate"].map(h => (
                    <th key={h} style={{
                      padding: "10px 14px", textAlign: "left",
                      fontSize: "clamp(0.58rem,1.1vw,0.65rem)",
                      fontWeight: 700, color: "#64748b",
                      textTransform: "uppercase", letterSpacing: "0.07em",
                      borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["2026-03-01", 145, 89,  234, "92%"],
                  ["2026-03-02", 132, 95,  227, "91%"],
                  ["2026-03-03", 156, 78,  234, "94%"],
                  ["2026-03-04", 168, 102, 270, "95%"],
                ].map(([date, a, b, c, rate], i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", transition: "background .15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f0f4f8"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <td style={{ padding: "10px 14px", color: "#0d1b2a", fontWeight: 500 }}>{date}</td>
                    <td style={{ padding: "10px 14px", color: "#374151" }}>{a}</td>
                    <td style={{ padding: "10px 14px", color: "#374151" }}>{b}</td>
                    <td style={{ padding: "10px 14px", color: "#374151", fontWeight: 600 }}>{c}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "2px 10px", borderRadius: 20,
                        background: `${GOV.green}12`,
                        border: `1px solid ${GOV.green}25`,
                        color: GOV.green,
                        fontSize: "0.72rem", fontWeight: 700,
                      }}>
                        {rate}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </>
  );
}
